import { useEffect } from 'react';
import * as Sentry from "@sentry/react";
import { useNavigationType, createRoutesFromChildren, matchRoutes, useLocation } from 'react-router-dom/dist';
import { AppSettings } from '../AppSettings';
import { createSentryAnonymousUser } from '../errors/errors';
import { SentrySettings } from '../api/dtos';

/**
 * Initializes Sentry with the provided settings.
 * Safe to call multiple times — will no-op if Sentry is already initialized.
 * Intended to be called both before React renders (in main.tsx, using window.APPSETTINGS)
 * and after the API responds (via useSentryInitializer), so that errors during the
 * initial render are captured even before the API call completes.
 */
const initializeSentry = (sentrySettings: SentrySettings): void => {
    if (!sentrySettings.dsn) return;

    // Guard: Sentry.getClient() returns undefined until Sentry.init() is called.
    if (Sentry.getClient()) {
        return;
    }

    const integrations = [
        // React Router V6 integration.
        // Helps with tracing URL changes and which URLs belong to the
        // same route, for example:
        //    - https://test.com/form/1
        //    - https://test.com/form/2
        // both belong to the same route */form/*
        // Note: this already includes browser tracing internally, so a
        // separate browserTracingIntegration() must NOT be added — both share
        // the same integration name and Sentry keeps only the last duplicate,
        // which would drop the router hooks and break wrapCreateBrowserRouterV6.
        Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect: useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),
        // Replay integration
        // Records user actions and sends to Sentry.
        Sentry.replayIntegration({
            // Whether to mask all text on the page when recording user actions.
            // When false only inputs are masked.
            // There is a separate setting to force unmasked inputs.
            maskAllText: false,
            // Whether to mask all images on the page when recording user actions.
            blockAllMedia: false,
            // Which URLs can the replay run on.
            // URL must contain one of the strings in the array or match one of
            // the regexes supplied.
            networkDetailAllowUrls: [window.location.origin],
            // Capture the body of network requests.
            // Sentry will still run data scrubbing on the bodies.
            networkCaptureBodies: true
        })
    ];

    if (sentrySettings.enableReportABug) {
        integrations.push(Sentry.feedbackIntegration({
            // Disable the injection of the default widget
            autoInject: false,
            colorScheme: 'system',
            showBranding: false,
            showName: false,
            showEmail: false,
            tags: {
                Application: AppSettings.sentry.application || 'Conference',
                Customer: sentrySettings.customer || 'BCT'
            },
            useSentryUser: {
                name: 'fullName',
                email: 'email',
            },
        }));
    }

    Sentry.init({
        // The DSN to use when sending information to Sentry.
        // Identifies a specific project in Sentry.
        dsn: sentrySettings.dsn,
        // Whether to send identifying information to Sentry such as
        // username, useragent, ip, etc.
        sendDefaultPii: sentrySettings.sendDefaultPii,
        // Whether to send available performance metrics to Sentry.
        enableMetrics: sentrySettings.enableMetrics,
        // Whether to attach stacktraces to actions sent to Sentry.
        attachStacktrace: sentrySettings.attachStacktrace,
        // Whether @sentry/react is in debug mode, logging more information
        // to the console.
        debug: sentrySettings.debug,
        // The environment to associate all actions sent to Sentry with.
        environment: sentrySettings.environment,
        // Release to associate all actions sent to Sentry with.
        release: AppSettings.sentry.release,
        // Percentage of captured traces that are sent to Sentry.
        // 1.0 = 100%, 0 = 0%
        tracesSampleRate: sentrySettings.tracesSampleRate,
        // Which URLs to trace / record in a trace.
        // Only trace internal URLs — don't want to trace every SEO url under the sun.
        tracePropagationTargets: [
            'localhost',
            /^\//,
            new RegExp(`/^${window.location.origin}`)
        ],
        // Percentage of captured session replays that are sent to Sentry.
        // 1.0 = 100%, 0 = 0%
        replaysSessionSampleRate: sentrySettings.replaysSessionSampleRate,
        // Percentage of captured session replays sent to Sentry when an error occurs.
        // 1.0 = 100%, 0 = 0%
        replaysOnErrorSampleRate: sentrySettings.replaysOnErrorSampleRate,
        // Enable logs to be sent to Sentry.
        enableLogs: sentrySettings.enableLogs,
        integrations,
    });

    Sentry.setTag('Application', AppSettings.sentry.application || 'Conference');
    Sentry.setTag('Customer', sentrySettings.customer || 'BCT');
    // Set user to anonymous at start up; will be overridden by useSentryUser
    // once user information is available.
    Sentry.setUser(createSentryAnonymousUser());
};

export default initializeSentry;
